import React from "react";
import { Alert, Box, List, Typography, Divider, styled } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import { useGetOrderByIdQuery } from "../../services/payments/payments";
import { Order, OrderItem } from "../../types/types";
import { PageLayout } from "../../components/PageLayout/PageLayout";
import {
  CommonBoxStyles,
  DiscountBadge,
  FlexBetweenBox,
  OriginalPrice,
} from "../shared/styledComponents";
import Footer from "../../components/Footer/Footer";
import ProcessOrderButton from "../../components/ProcessOrderButton/ProcessOrderButton";
import CartTotals from "../../components/CartTotals/CartTotals";
import { formatCOP, formatDateSpanish } from "../../utils/format";
import { resolveAssetUrl } from "../../utils/assets";
import { ORDER_TEXTS } from "../shared/constants";
import OrderDelivery from "./OrderDelivery";
import WhatsAppHandoff from "./WhatsAppHandoff";

// ── Styled components ─────────────────────────────────────────────────
const OrderPageContainer = styled(Box)(({ theme }) => ({
  margin: theme.spacing(1),
  padding: theme.spacing(1),
}));

const OrderContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  backgroundColor: theme.palette.common.white,
}));

const OrderItemCard = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1),
  boxShadow: theme.shadows[1],
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  backgroundColor: theme.palette.common.white,
}));

const OrderItemImage = styled("img")({
  width: 60,
  height: 80,
  objectFit: "cover",
  borderRadius: 8,
});

const OrderItemDetails = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
});

// ── OrderItemDisplay ──────────────────────────────────────────────────
const OrderItemDisplay: React.FC<{ item: OrderItem }> = ({ item }) => {
  const hasDiscount = item.discount != null && item.discount > 0;
  const effectivePrice = item.discounted_price ?? item.price;
  const lineSubtotal = effectivePrice * item.quantity;

  return (
    <OrderItemCard>
      {item.picture && (
        <OrderItemImage src={resolveAssetUrl(item.picture)} alt={item.name} loading="lazy" />
      )}
      <OrderItemDetails>
        {item.item_id ? (
          <Link to={`/producto/${item.item_id}`} style={{ textDecoration: "none" }}>
            <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
              {item.name}
            </Typography>
          </Link>
        ) : (
          <Typography variant="subtitle2" fontWeight="bold">
            {item.name}
          </Typography>
        )}
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {hasDiscount ? (
            <>
              <OriginalPrice variant="body2">{formatCOP(item.price)}</OriginalPrice>
              <Typography variant="body2" fontWeight="bold" color="success.main">
                {formatCOP(effectivePrice)}
              </Typography>
              <DiscountBadge label={`-${item.discount}%`} size="small" />
            </>
          ) : (
            <Typography variant="body2" fontWeight="bold">
              {formatCOP(item.price)}
            </Typography>
          )}
        </Box>
        <FlexBetweenBox>
          <Typography variant="body2">
            {ORDER_TEXTS.quantityLabel} {item.quantity}
          </Typography>
          <Typography variant="body2" fontWeight="bold" color="secondary">
            {formatCOP(lineSubtotal)}
          </Typography>
        </FlexBetweenBox>
      </OrderItemDetails>
    </OrderItemCard>
  );
};

// ── OrderDetails ──────────────────────────────────────────────────────
const OrderDetails: React.FC<{ order: Order }> = ({ order }) => (
  <Box>
    <Typography variant="h5" fontWeight="bold">
      {ORDER_TEXTS.title}
    </Typography>
    <Typography variant="subtitle1">
      {ORDER_TEXTS.clientLabel} {order.name}
    </Typography>
    <Typography variant="subtitle1">
      {ORDER_TEXTS.phoneLabel} {order.phone}
    </Typography>
    {order.order_time && (
      <Typography variant="subtitle1">
        {ORDER_TEXTS.dateLabel} {formatDateSpanish(order.order_time)}
      </Typography>
    )}
    {order.status && (
      <Typography variant="subtitle1">
        {ORDER_TEXTS.statusLabel} {order.status}
      </Typography>
    )}
    <Divider sx={{ my: 1 }} />
    <List disablePadding>
      {order.items.map((item, index) => (
        <OrderItemDisplay key={index} item={item} />
      ))}
    </List>
  </Box>
);

// ── OrderPage ─────────────────────────────────────────────────────────
/**
 * The money on the order, preferring what the server stored.
 *
 * `items_subtotal` is authoritative — it is what `POST /orders` computed and what the shop honours.
 * The reduce is the fallback for orders placed before EB-548, which carry no such field; without it
 * every historical order would render a subtotal of zero.
 */
const readTotals = (order: Order) => {
  const storedSubtotal = order.items_subtotal;
  const itemsSubtotal =
    typeof storedSubtotal === "number"
      ? storedSubtotal
      : order.items.reduce(
          (sum, item) => sum + (item.discounted_price ?? item.price) * item.quantity,
          0,
        );

  const deliveryFee = typeof order.delivery_fee === "number" ? order.delivery_fee : null;

  return { itemsSubtotal, deliveryFee, quoteItemCount: order.quote_item_count ?? 0 };
};

export const OrderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: order, isLoading } = useGetOrderByIdQuery(id ?? "");

  return (
    <PageLayout>
      <OrderPageContainer>
        <CommonBoxStyles>
          {isLoading ? (
            <Typography>{ORDER_TEXTS.loading}</Typography>
          ) : order ? (
            <OrderContentBox>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {ORDER_TEXTS.confirmedTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {ORDER_TEXTS.confirmedSubtitle}
              </Typography>

              <OrderDetails order={order} />
              <OrderDelivery order={order} />
              <Divider sx={{ my: 2 }} />

              {/* Stated plainly rather than absorbed silently: the shopper is about to send this
                  total to an advisor and needs to know it is not the one they last saw. */}
              {order.totals_adjusted && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {ORDER_TEXTS.totalsAdjusted}
                </Alert>
              )}

              <CartTotals {...readTotals(order)} />

              <WhatsAppHandoff orderId={order.id} />
            </OrderContentBox>
          ) : (
            <Typography>{ORDER_TEXTS.notFound}</Typography>
          )}
        </CommonBoxStyles>
      </OrderPageContainer>
      <Footer>
        <ProcessOrderButton />
      </Footer>
    </PageLayout>
  );
};

export default OrderPage;
